"use client";

import { type FC, useState } from "react";
import classNames from "classnames";

interface Props {
    type: "Fast" | "Reload" | "Settings" | "ChevronDown" | "Trash";
    isFastExchange?: boolean;
    link?: string;
    onClick?: (e?: any) => void;
    className?: string;
}

const IconButton: FC<Props> = ({ type, isFastExchange, onClick, link = "/swap", className }) => {
    if (type === "Fast") {
        return (
            <li className={"flex items-center " + className}>
                <button onClick={onClick}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <desc>Toggle fast exchange</desc>
                        <path
                            d="M20.4135 3.23869C20.3978 3.16835 20.3431 3.11365 20.2767 3.09411C17.9869 2.53533 12.7 4.52817 9.83584 7.39237C9.32395 7.90035 8.85896 8.45131 8.44476 9.03743C7.56167 8.95928 6.67857 9.02571 5.92442 9.35394C3.79873 10.2878 3.18135 12.73 3.00551 13.7772C2.97034 13.9804 3.1071 14.1758 3.3142 14.211C3.34937 14.2149 3.38454 14.2188 3.41971 14.2149L6.83096 13.8398C6.83487 14.0977 6.8505 14.3556 6.87785 14.6095C6.89348 14.7854 6.97554 14.9534 7.10058 15.0784L8.42132 16.3953C8.54636 16.5203 8.71438 16.6024 8.89022 16.618C9.14421 16.6454 9.3982 16.661 9.65609 16.6649L9.28097 20.0722C9.25753 20.2793 9.40992 20.4669 9.61702 20.4864C9.65219 20.4904 9.68735 20.4904 9.71861 20.4825C10.7658 20.3145 13.2119 19.6971 14.1419 17.5714C14.4701 16.8173 14.5366 15.9381 14.4623 15.0589C15.0524 14.6447 15.6033 14.1758 16.1113 13.6678C18.9833 10.8114 20.9644 5.64181 20.4135 3.23869ZM15.8925 10.2566C15.1618 10.9873 13.9739 10.9912 13.2432 10.2566C12.5086 9.52587 12.5086 8.33799 13.2432 7.60338C13.9739 6.86876 15.1618 6.86876 15.8964 7.60338C16.6271 8.33799 16.6271 9.52587 15.8925 10.2566Z"
                            fill={isFastExchange ? "hsl(353, 100%, 58%)" : "white"}
                        />
                        <path
                            opacity="0.5"
                            d="M8.3316 17.3487C8.11668 17.5636 7.77282 17.6457 7.35863 17.7199C6.42864 17.8762 5.60806 17.0752 5.77608 16.1374C5.8386 15.7818 6.03007 15.2816 6.1473 15.1644C6.21373 15.098 6.21763 14.9886 6.15121 14.9221C6.11213 14.883 6.06133 14.8635 6.00663 14.8713C5.48693 14.9338 5.0024 15.1722 4.63509 15.5395C3.71292 16.4617 3.62695 19.8729 3.62695 19.8729C3.62695 19.8729 7.04212 19.787 7.96038 18.8648C8.3316 18.4936 8.56605 18.013 8.62857 17.4894C8.6442 17.3292 8.44491 17.2315 8.3316 17.3487Z"
                            fill={isFastExchange ? "hsl(353, 100%, 58%, .5)" : "white"}
                        />
                    </svg>
                </button>
            </li>
        );
    }

    if (type === "Reload") {
        const [spin, setSpin] = useState(false);
        const handleOnClick = async () => {
            if (spin) return;
            try {
                const maxWaitTime = 1000;
                setSpin(true);

                if (!onClick) return;
                const timeBefore = Date.now();
                await onClick();
                const timeAfter = Date.now();

                const timeDiff = timeAfter - timeBefore;

                const waitTime = timeDiff < maxWaitTime ? maxWaitTime - timeDiff : maxWaitTime * 2 - timeDiff;

                await new Promise((resolve) => setTimeout(resolve, waitTime));
            } finally {
                setSpin(false);
            }
            setSpin(false);
        };

        return (
            <li className={"flex items-center " + className}>
                <button onClick={handleOnClick} tabIndex={0} type="button">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <desc>Update exchange rate</desc>
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M18.5543 7.35092C18.9129 7.14351 19.3717 7.26603 19.5791 7.62458C20.324 8.91235 20.7502 10.4076 20.7502 12.0001C20.7502 16.8326 16.8326 20.7501 12.0002 20.7501C11.0615 20.7501 10.1567 20.6021 9.30829 20.328L9.84382 21.733C9.99136 22.12 9.79719 22.5534 9.41014 22.7009C9.02309 22.8485 8.58973 22.6543 8.44219 22.2673L7.26407 19.1765C7.15073 18.8792 7.23735 18.5427 7.48016 18.337C7.72297 18.1314 8.06912 18.1013 8.34374 18.2621C9.41656 18.89 10.6652 19.2501 12.0002 19.2501C16.0042 19.2501 19.2502 16.0042 19.2502 12.0001C19.2502 10.6783 18.8971 9.44128 18.2807 8.37567C18.0733 8.01712 18.1958 7.55833 18.5543 7.35092Z"
                            fill="white"
                        />
                        <path
                            opacity="0.4"
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M14.59 1.29939C14.9771 1.15186 15.4104 1.34603 15.558 1.73308L16.7361 4.82384C16.8494 5.12116 16.7628 5.45764 16.52 5.66328C16.2771 5.86893 15.931 5.89897 15.6564 5.73824C14.5836 5.11032 13.335 4.7502 12 4.7502C7.99594 4.7502 4.75 7.99614 4.75 12.0002C4.75 13.322 5.10304 14.559 5.71946 15.6247C5.92687 15.9832 5.80435 16.442 5.4458 16.6494C5.08726 16.8568 4.62846 16.7343 4.42105 16.3757C3.67612 15.088 3.25 13.5927 3.25 12.0002C3.25 7.16771 7.16751 3.2502 12 3.2502C12.9386 3.2502 13.8434 3.39822 14.6918 3.67229L14.1563 2.26733C14.0088 1.88028 14.203 1.44692 14.59 1.29939Z"
                            fill="white"
                        />
                    </svg>
                </button>
            </li>
        );
    }

    if (type === "ChevronDown") {
        return (
            <li className={"flex items-center " + className}>
                <button onClick={onClick} tabIndex={0} type="button">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <desc>Chevron down</desc>
                        <path
                            d="M7.41 10.59C7.02 10.2 7.02 9.57 7.41 9.18C7.8 8.79 8.43 8.79 8.82 9.18L12 12.34L15.18 9.18C15.57 8.79 16.2 8.79 16.59 9.18C16.98 9.57 16.98 10.2 16.59 10.59L12.7 14.48C12.31 14.87 11.68 14.87 11.29 14.48L7.41 10.59Z"
                            fill="white"
                        />
                    </svg>
                </button>
            </li>
        );
    }

    if (type === "Trash") {
        return (
            <li className={"flex items-center " + className}>
                <button onClick={onClick} tabIndex={0} type="button">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <desc>Delete item</desc>
                        <path
                            d="M21 6H16V4.33C15.9765 3.68982 15.7002 3.08506 15.2316 2.64829C14.7629 2.21153 14.1402 1.97839 13.5 2H10.5C9.85975 1.97839 9.23706 2.21153 8.76843 2.64829C8.2998 3.08506 8.02346 3.68982 8 4.33V6H3C2.73478 6 2.48043 6.10536 2.29289 6.29289C2.10536 6.48043 2 6.73478 2 7C2 7.26522 2.10536 7.51957 2.29289 7.70711C2.48043 7.89464 2.73478 8 3 8H4V19C4 19.7956 4.31607 20.5587 4.87868 21.1213C5.44129 21.6839 6.20435 22 7 22H17C17.7956 22 18.5587 21.6839 19.1213 21.1213C19.6839 20.5587 20 19.7956 20 19V8H21C21.2652 8 21.5196 7.89464 21.7071 7.70711C21.8946 7.51957 22 7.26522 22 7C22 6.73478 21.8946 6.48043 21.7071 6.29289C21.5196 6.10536 21.2652 6 21 6ZM10 4.33C10 4.17 10.21 4 10.5 4H13.5C13.79 4 14 4.17 14 4.33V6H10V4.33ZM18 19C18 19.2652 17.8946 19.5196 17.7071 19.7071C17.5196 19.8946 17.2652 20 17 20H7C6.73478 20 6.48043 19.8946 6.29289 19.7071C6.10536 19.5196 6 19.2652 6 19V8H18V19Z"
                            fill="currentColor"
                        />
                        <path
                            d="M10 17C10.2652 17 10.5196 16.8946 10.7071 16.7071C10.8946 16.5196 11 16.2652 11 16V12C11 11.7348 10.8946 11.4804 10.7071 11.2929C10.5196 11.1054 10.2652 11 10 11C9.73478 11 9.48043 11.1054 9.29289 11.2929C9.10536 11.4804 9 11.7348 9 12V16C9 16.2652 9.10536 16.5196 9.29289 16.7071C9.48043 16.8946 9.73478 17 10 17Z"
                            fill="currentColor"
                        />
                        <path
                            d="M14 17C14.2652 17 14.5196 16.8946 14.7071 16.7071C14.8946 16.5196 15 16.2652 15 16V12C15 11.7348 14.8946 11.4804 14.7071 11.2929C14.5196 11.1054 14.2652 11 14 11C13.7348 11 13.4804 11.1054 13.2929 11.2929C13.1054 11.4804 13 11.7348 13 12V16C13 16.2652 13.1054 16.5196 13.2929 16.7071C13.4804 16.8946 13.7348 17 14 17Z"
                            fill="currentColor"
                        />
                    </svg>
                </button>
            </li>
        );
    }

    const [spin, setSpin] = useState(false);

    const handleOnClickSettings = async () => {
        if (onClick) {
            onClick();
        }
    };

    return (
        <li className={"flex items-center " + className}>
            <button onClick={handleOnClickSettings} tabIndex={0} type="button">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <desc>Open settings</desc>
                    <path
                        opacity="0.4"
                        d="M12.9545 3H11.0455C9.99109 3 9.13635 3.80589 9.13635 4.8C9.13635 5.93761 7.91917 6.66087 6.92 6.11697L6.81852 6.06172C5.90541 5.56467 4.73782 5.85964 4.21064 6.72057L3.25609 8.27942C2.72891 9.14034 3.04176 10.2412 3.95487 10.7383C4.95451 11.2824 4.95451 12.7176 3.95487 13.2617C3.04176 13.7588 2.72891 14.8597 3.25609 15.7206L4.21064 17.2794C4.73782 18.1404 5.90541 18.4353 6.81851 17.9383L6.92 17.883C7.91917 17.3391 9.13635 18.0624 9.13635 19.2C9.13635 20.1941 9.99109 21 11.0455 21H12.9545C14.0089 21 14.8636 20.1941 14.8636 19.2C14.8636 18.0624 16.0808 17.3391 17.08 17.883L17.1815 17.9383C18.0946 18.4353 19.2622 18.1403 19.7894 17.2794L20.7439 15.7206C21.2711 14.8596 20.9582 13.7588 20.0451 13.2617C19.0455 12.7176 19.0455 11.2824 20.0451 10.7383C20.9582 10.2412 21.2711 9.14036 20.7439 8.27943L19.7894 6.72058C19.2622 5.85966 18.0946 5.56468 17.1815 6.06174L17.08 6.11698C16.0808 6.66088 14.8636 5.93762 14.8636 4.8C14.8636 3.80589 14.0089 3 12.9545 3Z"
                        fill="white"
                    />
                    <circle cx="12" cy="12" r="3" fill="white" />
                </svg>
            </button>
        </li>
    );
};

export default IconButton;
